/**
 * Action and the answer bank (.scratch/action-tab/spec.md 9 and 9.1; action-tab
 * ticket 16). Signed-in only. One function per route; the shapes are spec 9.1's,
 * in `types.ts`. Nothing here is sent to an employer: the kit is what the person
 * pastes into the employer's own form.
 */
import { api } from './client';
import type { ActionKit, ActionQuestionsResponse, ActionResponse, ActionRetryResponse, ActionSuggestResponse, AnswerPutRequest, AnswerValue, AnswerWriteResponse, AnswersResponse } from './types';

const job = (jobId: string) => `/v1/action/${encodeURIComponent(jobId)}`;
// A `label:` key may hold any character, so the key is encoded whole and the server's route takes the rest of the path.
const answer = (answerKey: string) => `/v1/answers/${encodeURIComponent(answerKey)}`;

export const actionApi = {
  /**
   * The To do page (50 rows, by state then newest), the Applied list (`sent` rows of the last 7 days, at
   * most 20) and the header's counts over the whole list. `cursor` is the previous page's `nextCursor`.
   */
  list: (cursor?: string | null) => api<ActionResponse>('/v1/action', { query: { cursor: cursor ?? undefined } }),
  /** One row's apply kit: the résumé's change lines, the form's fields in order with rendered values, and `copyAll`. */
  kit: (jobId: string) => api<ActionKit>(`${job(jobId)}/kit`),
  /** Every open question across the rows: banked ones once each, most-unlocking first, then the job-scoped ones by job. */
  questions: () => api<ActionQuestionsResponse>('/v1/action/questions'),
  /** An answer kept for this job only (an employer-specific question, or one asked with the employer's own options). */
  answerForJob: (jobId: string, qhash: string, value: AnswerValue) =>
    api<AnswerWriteResponse>(`${job(jobId)}/answers/${encodeURIComponent(qhash)}`, { method: 'PUT', body: { value } }),
  /** Proposed answers for this job's open questions, never a sensitive or a `textarea` one. Nothing is written until the person confirms. */
  suggest: (jobId: string) => api<ActionSuggestResponse>(`${job(jobId)}/suggest`, { method: 'POST' }),
  /** Records the first Apply ↗ click (204). A repeat changes nothing. */
  opened: (jobId: string) => api<void>(`${job(jobId)}/opened`, { method: 'POST' }),
  /**
   * A new tailor run for a `failed` row, at the same level from the same résumé. It reserves one
   * credit, hence the `Idempotency-Key`, and answers 402 `insufficient_credits` with nothing written.
   */
  retry: (jobId: string, idempotencyKey: string) => api<ActionRetryResponse>(`${job(jobId)}/retry`, { method: 'POST', idempotencyKey }),
  /**
   * Takes a `closed` or `failed` row off the list (204), or a pasted job's `ready` one (its "Yes, I applied" records a
   * manual application first). The server records it as a `pass`, so the job is no longer a row and never comes back to
   * the deck. 409 for a row in any other state.
   */
  remove: (jobId: string) => api<void>(job(jobId), { method: 'DELETE' }),
};

export const answersApi = {
  /** The whole bank and how many times its answers have been reused. */
  list: () => api<AnswersResponse>('/v1/answers'),
  /**
   * Writes one banked answer and re-resolves the rows; `changed` is the jobIds whose state moved.
   * `source` is `suggested` for a proposal confirmed untouched, `user` (the default) otherwise.
   * 400 for a key the server does not know, a choice outside the kind's values, or a text over 2,000 characters.
   */
  put: (answerKey: string, body: AnswerPutRequest) => api<AnswerWriteResponse>(answer(answerKey), { method: 'PUT', body }),
  /** Forgets one answer (204): asked again the next time a form needs it. Kits already recorded keep theirs. */
  remove: (answerKey: string) => api<void>(answer(answerKey), { method: 'DELETE' }),
};
